import { ComponentType, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Loading from "Components/Loading/Loading";
import { authService, userService } from "Apis";
import { saveAuthToken } from "Helpers/saveAuthToken";
import { loginSuccess, setUser } from "Actions";
import { useUser } from "Hooks/useUser";
import { ApiShareParameters } from "@incendium/api";

type SharedTokenInjectedProps = {
  shareToken?: string | null;
  shareTokenError?: string | null;
  shareTokenLoading?: boolean;
  shareTokenPayload?: ApiShareParameters | null;
};

type WithSharedTokenOptions = {
  showLoading?: boolean;
};

function withSharedToken<P extends SharedTokenInjectedProps>(
  WrappedComponent: ComponentType<P>,
  options?: WithSharedTokenOptions
) {
  const { showLoading = true } = options || {};

  function WithSharedToken(props: Omit<P, keyof SharedTokenInjectedProps>) {
    const location = useLocation();
    const params = useParams<{ token?: string }>();
    const dispatch = useDispatch();
    const { user } = useUser();
    const validatedTokenRef = useRef<string | null>(null);

    const [shareTokenError, setShareTokenError] = useState<string | null>(null);
    const [shareTokenLoading, setShareTokenLoading] = useState(false);
    const [shareTokenPayload, setShareTokenPayload] =
      useState<ApiShareParameters | null>(null);

    const shareToken = useMemo(() => {
      if (params.token) {
        return params.token;
      }
      const search = new URLSearchParams(location.search);
      return search.get("token");
    }, [params.token, location.search]);

    useEffect(() => {
      let mounted = true;

      const validate = async (token: string) => {
        setShareTokenLoading(true);
        try {
          const payload = await authService.authValidateToken({
            body: { token },
          });

          if (!payload) {
            throw new Error("Invalid share token");
          }

          if (!user) {
            const authPayload = { token };
            saveAuthToken(authPayload);
            const authenticatedUser = await userService.userServiceGetAuthUser();
            dispatch(setUser(authenticatedUser));
            dispatch(loginSuccess(authPayload));
          }

          if (mounted) {
            setShareTokenError(null);
            setShareTokenPayload(payload);
            validatedTokenRef.current = token;
          }
        } catch (error) {
          if (mounted) {
            setShareTokenError(
              "Failed to validate share link. Please request a new link."
            );
            setShareTokenPayload(null);
            validatedTokenRef.current = token;
          }
        } finally {
          if (mounted) {
            setShareTokenLoading(false);
          }
        }
      };

      if (shareToken) {
        if (validatedTokenRef.current === shareToken) {
          return;
        }
        validate(shareToken);
      } else {
        setShareTokenError("Missing share token.");
        setShareTokenPayload(null);
        validatedTokenRef.current = null;
      }

      return () => {
        mounted = false;
      };
    }, [dispatch, shareToken, user]);

    if (shareTokenLoading && showLoading) {
      return <Loading fullHeight text="Validating share link..." />;
    }

    return (
      <WrappedComponent
        {...(props as P)}
        shareToken={shareToken}
        shareTokenError={shareTokenError}
        shareTokenLoading={shareTokenLoading}
        shareTokenPayload={shareTokenPayload}
      />
    );
  }

  WithSharedToken.displayName = `WithSharedToken(${WrappedComponent.displayName || WrappedComponent.name || "Component"
    })`;

  return WithSharedToken;
}

export type { SharedTokenInjectedProps };
export default withSharedToken;

